import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import apiClient from '../ApiClient/ApiClient';

export default function ShipmentAdjustments({ shipNum, selectedRows, onComplete }) {
  const [open, setOpen] = useState(false);
  const [inputs, setInputs] = useState([]);
  const [reason, setReason] = useState('');
  const [batches, setBatches] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState('Quantity');
  const openEditor = async (nextKind) => {
    setBusy(true);
    try {
      const response = await apiClient.get('/ToShip/BillingAllocations', { params: { shipNum } });
      const details = response.data?.data || [];
      setInputs(selectedRows.map(row => {
        const detail = details.find(x => x.lineId === row.id);
        if (!detail) throw new Error('部分明細已變更，請重新查詢');
        return { id: row.id, drawNum: row.drawNum ?? row.圖號, oldQuantity: detail.quantity,
          newQuantity: String(detail.quantity), price: String(detail.price), oldAmount: detail.amount, amount: '', currency: detail.currency,
          allocations: (nextKind === 'Quantity' && detail.allocations.every(x => !x.billNo.trim()) ? [] : detail.allocations)
            .map(x => ({ ...x, quantity: String(x.quantity) })) };
      }));
      setKind(nextKind); setReason(''); setOpen(true);
    } catch (error) { alert(error.response?.data?.message || error.message || '讀取帳款分攤失敗'); }
    finally { setBusy(false); }
  };
  const load = async () => {
    setBusy(true);
    try {
      const response = await apiClient.get('/ToShip/Adjustments', { params: { shipNum } });
      setBatches(response.data?.data || []); setHistoryOpen(true);
    } catch (error) { alert(error.response?.data?.message || '查詢調整批次失敗'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post('/ToShip/SubmitAdjustment', { shipNum, kind, reason,
        lines: inputs.map(x => ({ lineId: x.id, quantity: Number(x.newQuantity), price: Number(x.price), amount: x.amount.trim() ? Number(x.amount) : null,
          allocations: x.allocations.map(a => ({ ...a, quantity: Number(a.quantity), taxPercent: Number(a.taxPercent) })) })) });
      alert('調整已送審，核准前原出貨數量與庫存不變。'); setOpen(false); await load();
    } catch (error) { alert(error.response?.data?.message || '送審失敗'); }
    finally { setBusy(false); }
  };
  const review = async (batch, approve) => {
    const reviewReason = approve ? '' : window.prompt('請輸入退回原因');
    if (!approve && !reviewReason?.trim()) return;
    if (approve && !window.confirm('確認核准此批次？將更新數量、有效庫存扣帳及訂單累計。')) return;
    setBusy(true);
    try {
      const response = await apiClient.post(`/ToShip/${approve ? 'ApproveAdjustment' : 'RejectAdjustment'}`, { batchId: batch.id, reason: reviewReason });
      const warnings = response.data?.data?.warnings || [];
      alert(`${approve ? '調整核准完成' : '調整已退回'}${warnings.length ? `\n${warnings.join('\n')}` : ''}`); await load(); await onComplete();
    } catch (error) { alert(error.response?.data?.message || '審核失敗'); }
    finally { setBusy(false); }
  };
  return <>
    <Button variant="contained" disabled={busy || !shipNum || selectedRows.length === 0} onClick={() => openEditor('Quantity')}>選取明細數量調整</Button>
    <Button variant="contained" disabled={busy || !shipNum || selectedRows.length === 0} onClick={() => openEditor('Accounts')}>選取明細拆帳／帳款調整</Button>
    <Button disabled={busy || !shipNum} onClick={load}>調整批次／審核</Button>
    <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>{kind === 'Quantity' ? '數量調整' : '拆帳／帳款調整'}送審－{shipNum}</DialogTitle>
      <DialogContent>
        <Typography>{kind === 'Quantity' ? '原核准資料保留，審核完成後才依有效扣帳量補扣或沖回。訂單累計數量差額須為整數。' : '純帳款調整不異動庫存；分攤數量合計須等於原出貨數量。'}</Typography>
        <Typography>發票號碼留白表示未請款；刪除全部分攤表示整筆轉回未請款。已結案發票須先解鎖。</Typography>
        <Typography>純拆帳保留原明細總金額，四捨五入尾差由最後一筆分攤承接。</Typography>
        {inputs.map(row => <Box key={row.id} sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography>{row.drawNum}（原數量 {row.oldQuantity}）</Typography>
          <TextField disabled={kind !== 'Quantity'} type="number" label="調整後數量" value={row.newQuantity} inputProps={{ min: 0, step: 1 }}
            onChange={(e) => setInputs(inputs.map(x => x.id === row.id ? { ...x, newQuantity: e.target.value } : x))} />
          <TextField disabled={kind !== 'Accounts'} type="number" label="單價" value={row.price} inputProps={{ min: 0, step: 0.0001 }}
            onChange={(e) => setInputs(inputs.map(x => x.id === row.id ? { ...x, price: e.target.value } : x))} />
          <TextField type="number" label="指定明細應收金額（可留白）" value={row.amount} inputProps={{ min: 0, step: 0.01 }}
            helperText={`原金額 ${row.oldAmount}；指定金額需要帳款修改權限`}
            onChange={(e) => setInputs(inputs.map(x => x.id === row.id ? { ...x, amount: e.target.value } : x))} />
          </Box>
          {row.allocations.map((allocation, index) => <Box key={index} sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {[
              ['billNo', '發票號碼（可留白）', 'text'], ['billDate', '發票日期 yyyy/MM/dd', 'text'], ['quantity', '分攤數量', 'number'], ['taxPercent', '原發票稅率 %', 'number']
            ].map(([field, label, type]) => <TextField key={field} label={label} type={type} value={allocation[field]}
              onChange={(e) => setInputs(inputs.map(x => x.id === row.id ? { ...x,
                allocations: x.allocations.map((a, i) => i === index ? { ...a, [field]: e.target.value } : a) } : x))} />)}
            <Button onClick={() => setInputs(inputs.map(x => x.id === row.id ? { ...x, allocations: x.allocations.filter((a, i) => i !== index) } : x))}>刪除分攤</Button>
          </Box>)}
          <Button onClick={() => setInputs(inputs.map(x => x.id === row.id ? { ...x, allocations: [...x.allocations,
            { billNo: '', billDate: new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' }).replaceAll('-', '/'), taxPercent: x.currency === 'TWD' ? 5 : 0,
              quantity: String(Math.max(0, Number(x.newQuantity) - x.allocations.reduce((sum, a) => sum + Number(a.quantity), 0))) }] } : x))}>增加數量分攤</Button>
          {row.allocations.length > 0 && <Typography>分攤合計：{row.allocations.reduce((sum, a) => sum + Number(a.quantity), 0)}／出貨數量：{row.newQuantity}</Typography>}
        </Box>)}
        <TextField fullWidth multiline label="調整原因" value={reason} inputProps={{ maxLength: 500 }} onChange={(e) => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setOpen(false)}>關閉</Button>
        <Button disabled={busy || !reason.trim() || inputs.some(x => x.newQuantity.trim() === '' || !Number.isFinite(Number(x.newQuantity)) || Number(x.newQuantity) < 0 || x.price.trim() === '' || !Number.isFinite(Number(x.price)) || Number(x.price) < 0
          || (x.amount.trim() !== '' && (!Number.isFinite(Number(x.amount)) || Number(x.amount) < 0))
          || x.allocations.some(a => !Number.isFinite(Number(a.quantity)) || Number(a.quantity) <= 0 || a.taxPercent === '' || !Number.isFinite(Number(a.taxPercent)) || Number(a.taxPercent) < 0 || Number(a.taxPercent) > 100)
          || (x.allocations.length > 0 && Math.abs(x.allocations.reduce((sum, a) => sum + Number(a.quantity), 0) - Number(x.newQuantity)) > 0.000001))} onClick={submit}>送審</Button></DialogActions>
    </Dialog>
    <Dialog open={historyOpen} onClose={() => !busy && setHistoryOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle>調整批次－{shipNum}</DialogTitle>
      <DialogContent>
        {batches.length === 0 && <Typography>查無調整批次</Typography>}
        {batches.map(item => <Box key={item.batch.id} sx={{ my: 2 }}>
          <Typography>{item.batch.id}／{item.batch.kind === 'Accounts' ? '帳款調整' : '數量調整'}／{item.batch.status}／送審：{item.batch.submittedBy}／核准或退回：{item.batch.reviewedBy || '尚未審核'}</Typography>
          <Typography>原因：{item.batch.reason} {item.batch.reviewReason}</Typography>
          <Typography>台北時間：送審 {item.batch.submittedAt?.replace('T', ' ').slice(0, 19)}／審核 {item.batch.reviewedAt?.replace('T', ' ').slice(0, 19) || '尚未審核'}</Typography>
          <Box sx={{ height: 220 }}><DataGrid rows={item.lines} columns={[
            { field: 'shipmentLineId', headerName: '原明細ID', width: 110 },
            { field: 'oldQuantity', headerName: '原數量', width: 110 },
            { field: 'newQuantity', headerName: '調整數量', width: 110 },
            { field: 'oldPostedQuantity', headerName: '送審時已扣量', width: 140, valueGetter: (params) => item.batch.kind === 'Accounts' ? '不異動庫存' : params.value },
            { field: 'oldPrice', headerName: '原單價', width: 110 },
            { field: 'newPrice', headerName: '調整單價', width: 110 },
            { field: 'oldAmount', headerName: '原應收金額', width: 120 },
            { field: 'newAmount', headerName: '調整應收金額', width: 130 },
            { field: 'status', headerName: '狀態', width: 110 }
          ]} disableRowSelectionOnClick /></Box>
          {item.lines.map(line => <Typography key={line.id}>明細 {line.shipmentLineId} 新分攤：{JSON.parse(line.newAllocationsJson || '[]').map(a => `${a.BillNo || '未請款'}：數量 ${a.Quantity}／稅率 ${a.TaxPercent}%／${a.BillDate || '未指定日期'}`).join('；') || '未請款'}</Typography>)}
          {item.batch.status === 'Pending' && <><Button disabled={busy} onClick={() => review(item.batch, true)}>核准（需核准權限）</Button>
            <Button disabled={busy} onClick={() => review(item.batch, false)}>退回</Button></>}
        </Box>)}
      </DialogContent><DialogActions><Button disabled={busy} onClick={() => setHistoryOpen(false)}>關閉</Button></DialogActions>
    </Dialog>
  </>;
}
